import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Toast } from 'antd-mobile';
import { useI18n } from '../../../hooks/useI18n';
import {
  getSupervisorScenarioDetail,
  SupervisorScenarioInfo
} from '@/lib/api/ai-supervisor';
import { request } from '@/lib/api';
import ManyDotLoading from '@/components/Loaders/ManyDotLoading';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';
import xunzhang from '@/assets/images/ai/xunzhang.png';

interface AnalysisRecord {
  id: string;
  overall_score: number;
  passed_status: 'passed' | 'failed';
  task_status: 'pending' | 'processing' | 'completed' | 'failed';
  user_name: string;
  duration: number;
  created_at: string;
  content_file_name?: string;
  error_msg?: string;
}

const AiSupervisorRecords: React.FC = () => {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [scenario, setScenario] = useState<SupervisorScenarioInfo | null>(null);
  const [records, setRecords] = useState<AnalysisRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showFailedModal, setShowFailedModal] = useState<boolean>(false);
  const [failedRecord, setFailedRecord] = useState<AnalysisRecord | null>(null);

  // 从URL获取userId和embedded参数
  const userId = searchParams.get('userId');
  const embedded = searchParams.get('embedded');

  // 获取场景详情
  const fetchScenarioDetail = async () => {
    if (!scenarioId) return;

    try {
      const response = await getSupervisorScenarioDetail(scenarioId);
      if (response.success && response.data) {
        setScenario(response.data);
      }
    } catch (error) {
      console.error('获取场景详情失败:', error);
    }
  };

  // 获取分析记录列表
  const fetchAnalysisRecords = async () => {
    if (!scenarioId) return;

    setLoading(true);
    try {
      const endpoint = `/supervisor-analyses/by-scenario/${scenarioId}`;
      const params: any = { page: 1, pageSize: 100 };
      if (userId) {
        params.user_id = userId;
      }

      const response = await request<any>(endpoint, {
        method: 'GET',
        params
      });

      if (response.success && response.data) {
        setRecords(response.data.list || []);
      } else {
        Toast.show({ content: '获取分析记录失败', icon: 'fail' });
      }
    } catch (error) {
      console.error('获取分析记录失败:', error);
      Toast.show({ content: '获取分析记录失败', icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarioDetail();
    fetchAnalysisRecords();
  }, [scenarioId, userId]);

  // 查看分析详情
  const handleViewDetail = (record: AnalysisRecord) => {
    // 如果任务状态为失败，显示失败弹窗
    if (record.task_status === 'failed') {
      setFailedRecord(record);
      setShowFailedModal(true);
      return;
    }

    // 如果任务状态为待处理或分析中，弹窗提示等待
    if (record.task_status === 'pending' || record.task_status === 'processing') {
      Toast.show({
        content: record.task_status === 'pending' ? '分析任务待处理中，请稍后查看' : '分析任务进行中，请稍后查看',
        icon: 'loading',
        duration: 2000
      });
      return;
    }

    // 只有completed状态才允许进入详情
    const queryParams = new URLSearchParams();
    queryParams.append('analysisId', record.id);
    if (embedded) {
      queryParams.append('embedded', embedded);
    }
    navigate(`/ai-supervisor/analysis?${queryParams.toString()}`);
  };

  // 再次上传
  const handlePracticeAgain = () => {
    navigate(`/ai-supervisor/upload/${scenarioId}`);
  };

  // 格式化时间
  const formatDateTime = (dateTimeStr: string) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PCMaxWidthContainer bgGradient='bg-[#f5f7fa]'>
    <div className='bg-[#f5f7fa]'>

      {/* 失败弹窗 */}
      {showFailedModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={() => {
            setShowFailedModal(false);
            setFailedRecord(null);
          }}
        >
          <div
            className='bg-white rounded-[12px] w-[300px] mx-4'
            onClick={(e) => e.stopPropagation()}
          >
            {/* 图标和标题 */}
            <div className='flex flex-col items-center pt-8 pb-6 px-4'>
              <div className='w-[60px] h-[60px] rounded-full bg-[#fff1f0] flex items-center justify-center mb-4'>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="12" stroke="#f53f3f" strokeWidth="2"/>
                  <path d="M16 10V18" stroke="#f53f3f" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="16" cy="22" r="1.5" fill="#f53f3f"/>
                </svg>
              </div>
              <h3 className='text-[#0a1b39] text-[18px] font-medium mb-2'>分析失败</h3>
              <p className='text-[#86909c] text-[14px] text-center'>
                {failedRecord?.error_msg || '未知错误，请重试'}
              </p>
            </div>

            {/* 按钮组 */}
            <div className='px-4 pb-4 space-y-2'>
              <button
                onClick={() => {
                  setShowFailedModal(false);
                  setFailedRecord(null);
                  handlePracticeAgain();
                }}
                className='w-full h-[44px] bg-gradient-to-r from-[#7b72ff] to-[#998dff] text-white text-[16px] rounded-[8px] font-medium'
              >
                重新上传
              </button>
              <button
                onClick={() => {
                  setShowFailedModal(false);
                  setFailedRecord(null);
                }}
                className='w-full h-[44px] bg-[#f2f3f5] text-[#86909c] text-[16px] rounded-[8px] font-medium'
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 主体内容 */}
      <div className='px-[12px] pb-24 pt-6'>
        {loading ? (
          <div className='flex items-center justify-center py-20'>
            <ManyDotLoading />
          </div>
        ) : records.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-20'>
            <div className='text-[#909399] text-sm mt-4'>暂无分析记录</div>
            <div className='text-[#c0c4cc] text-xs mt-2'>
              上传音视频后开始分析
            </div>
          </div>
        ) : (
          <div className='space-y-[12px]'>
            {records.map((record, index) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => handleViewDetail(record)}
                className='backdrop-blur-[2px] bg-gradient-to-b from-white to-[rgba(255,255,255,0.5)] border border-white border-solid rounded-[8px] h-[135px] cursor-pointer active:scale-98 transition-transform overflow-hidden'
              >
                <div className='px-[15px] pt-[15px]'>
                  <div className='flex items-start gap-1 mb-3'>
                    {/* 奖杯图标 */}
                    <div className='w-[25px] h-[25px] flex-shrink-0'>
                      <img
                        src={
                          record.passed_status === 'passed'
                            ? xunzhang
                            : record.passed_status === 'failed'
                            ?xunzhang
                            : xunzhang
                        }
                        alt=""
                        className='w-full h-full'
                      />
                    </div>

                    {/* 得分 */}
                    <div className='flex-1'>
                      <p className='text-[#0a1b39] text-[16px] leading-[1.6] font-normal'>
                        总得分：{record.overall_score ?? '-'}
                      </p>
                    </div>

                    {/* 状态标签 */}
                    <div className='flex items-center gap-1'>
                      <div className='w-[5px] h-[5px] rounded-full' style={{
                        backgroundColor: record.task_status === 'completed'
                          ? (record.passed_status === 'passed'
                            ? '#15BC83'
                            : record.passed_status === 'failed'
                            ? '#998dff'
                            : '#86909c')
                          : record.task_status === 'failed'
                          ? '#f53f3f'
                          : '#86909c'
                      }}></div>
                      <span className='text-[14px] leading-[22px]' style={{
                        color: record.task_status === 'completed'
                          ? (record.passed_status === 'passed'
                            ? '#15BC83'
                            : record.passed_status === 'failed'
                            ? '#998dff'
                            : '#86909c')
                          : record.task_status === 'failed'
                          ? '#f53f3f'
                          : '#86909c'
                      }}>
                        {record.task_status === 'pending'
                          ? '待处理'
                          : record.task_status === 'processing'
                          ? '分析中'
                          : record.task_status === 'failed'
                          ? '失败'
                          : record.task_status === 'completed'
                          ? (record.passed_status === 'passed'
                            ? '已通过'
                            : record.passed_status === 'failed'
                            ? '未通过'
                            : '未通过')
                          : '未知状态'}
                      </span>
                    </div>
                  </div>

                  {/* 场景名称 */}
                  <p className='text-[#485066] text-[14px] leading-[1.6] mb-3 line-clamp-1'>
                    {/* {scenario?.name || '督导场景'} */}
                    {record.content_file_name ? `${record.content_file_name}` : ''}
                  </p>
                </div>

                {/* 分隔线 */}
                <div className='h-px bg-[#f2f3f5] mx-[15px]'></div>

                {/* 参与时间 */}
                <div className='px-[15px] pt-[13px]'>
                  <p className='text-[#909399] text-[12px] leading-[1.6]'>
                    参与时间：{formatDateTime(record.created_at)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
    </PCMaxWidthContainer>
  );
};

export default AiSupervisorRecords;
